import {CreateEditLedger} from './../../product-master/ledger/CreateEditLedger'
import axios, {AxiosResponse} from 'axios'
import {SetOffInvoiceResp} from './EnterTransactionsAPI'

const API_URL = process.env.REACT_APP_THEME_API_URL
// Helper function to convert snake_case to camelCase
export const toCamelCase = (str: string): string => {
  return str.replace(/_([a-z])/g, (g) => g[1].toUpperCase())
}

// Helper function to transform an object's keys from snake_case to camelCase
export const transformKeys = <T extends Record<string, any>>(obj: Record<string, any>): T => {
  const transformedObj: Record<string, any> = {}

  Object.entries(obj).forEach(([key, value]) => {
    const camelKey = toCamelCase(key)
    transformedObj[camelKey] = value
  })

  return transformedObj as T
}

// Type Definitions
export interface Transaction {
  transactionId: number
  companyId: number
  voucherId: number
  voucherNumber: number
  currencyCode: string
  reportingCurrency: string
  exchangeMultiplier: number
  voucherDate: string
  totalAmount: number
  narration: string | null
  transactionSummary: string | null
  isNaReference: boolean
  naReferenceAmount: number
  voucherType: string
  voucherName: string | null

  // Derived from related data
  invoiceItem: {
    [transactionId: string]: {
      [ledgerId: string]: InvoiceItem[]
    }
  } | null

  advanceItem: {
    [transactionId: string]: {
      [ledgerId: string]: AdvanceItem[]
    }
  } | null
  againstInvoice: {
    [transactionId: string]: {
      [ledgerId: string]: AgainstInvoice[]
    }
  } | null
  againstAdvance: {
    [transactionId: string]: {
      [ledgerId: string]: AgainstAdvance[]
    }
  } | null

  transactionItems: TransactionItem[]
}

export interface LedgerData {
  ledgerId: number
  companyId: number
  groupId: number
  parentLedger: string
  subLedger: string | null
  ledgerCode: string
  isParentLedger: boolean
  currency: string
  email: string | null
  personName: string | null
  address: string | null
  state: string | null
  country: string | null
  postalCode: string | null
  accountNumber: string | null
  branchName: string | null
  ifscCode: string | null
  classification: string
  parentGroup: string
  subGroup: string | null
  openingBalance: number
  closingBalance: number
  creditAmount: number
  debitAmount: number
  currentBalance: number
  transactionType: 'debit' | 'credit'
  asOnDate: string
}

export interface AdvanceItem {
  advanceId: number
  transactionId: number
  ledgerId: number
  advanceNumber: number
  advanceDate: string
  amount: number
  transactionType: 'debit' | 'credit'
  description: string
  settledAmount: number
  lastSettledAmount: number
  isSettled: boolean
  details: any | null
}

export interface AgainstAdvance {
  againstAdvanceItemId: number
  advanceNumber: number
  advanceDate: string
  transactionId: number
  amount: number
  transactionType: string
  ledgerId: number
  advanceItemId: number
  setOffAmount: number
  settled_amount: number
  description: string
}

export interface InvoiceItem {
  invoiceId: number
  transactionId: number
  ledgerId: number
  invoiceNumber: number
  invoiceDate: string
  dueDate: string
  amount: number
  transactionType: 'debit' | 'credit'
  description: string
  settledAmount: number
  lastSettledAmount: number
  isSettled: boolean
  details: any | null
}

export interface AgainstInvoice {
  againstInvoiceItemId: number
  invoiceNumber: number
  transactionId: number
  ledgerId: number
  invoiceItemId: number
  setOffAmount: number
  description: string
  dueDate: string
  amount: number
  settledAmount: number
  transactionType: string
}

export interface TransactionItem {
  transactionItemId: number
  transactionId: number
  ledgerId: number
  transactionType: 'debit' | 'credit'
  transactionAmount: number
  userName: string
  parentLedger?: string | null
  subLedger?: string | null
  parentGroup?: string | null
  subGroup?: string | null
}

export interface ApiResponse {
  transactions: any[]

  closing_balance: number
  opening_balance: number
  total_credit: number
  total_debit: number
  ledger: Record<string, any>
  invoice_item: {
    [transactionId: string]: {
      [ledgerId: string]: Record<string, any>[]
    }
  }
  advance_item: {
    [transactionId: string]: {
      [ledgerId: string]: Record<string, any>[] // Changed to Record<string, any> to handle snake_case
    }
  }
  against_invoice: {
    [transactionId: string]: {
      [ledgerId: string]: Record<string, any>[] // Changed to Record<string, any> to handle snake_case
    }
  }
  against_advance: {
    [transactionId: string]: {
      [ledgerId: string]: Record<string, any>[] // Changed to Record<string, any> to handle snake_case
    }
  }
}

export interface TransformedResponse {
  transactions: Transaction[]
  ledgers: Record<string, LedgerData>
  openingBalance: number
  closingBalance: number
  debitAmount: number
  creditAmount: number
}

// Transformation Function
export const transformResponse = (apiResponse: ApiResponse): TransformedResponse => {
  // // console.log(apiResponse)
  // Transform transactions
  const transformedTransactions: Transaction[] = apiResponse.transactions.map((transaction) => {
    const transactionId = transaction.transaction_id

    // Get related items for this transaction
    let invoiceItem
    let advanceItem
    let againstAdvance
    let againstInvoice
    // Transform invoice_item
    if (apiResponse.invoice_item?.[transactionId]) {
      invoiceItem = Object.entries(apiResponse.invoice_item[transactionId]).reduce(
        (acc, [ledgerId, items]) => ({
          ...acc,
          [ledgerId]: items.map((item) => ({
            ...transformKeys<InvoiceItem>({
              invoice_id: item.invoice_id,
              transaction_id: item.transaction_id,
              ledger_id: item.ledger_id,
              invoice_number: item.invoice_number,
              invoice_date: item.invoice_date,
              due_date: item.due_date,
              amount: item.amount,
              transaction_type: item.transaction_type,
              description: item.description,
              settled_amount: item.settled_amount,
              last_settled_amount: item.last_settled_amount,
              is_settled: item.is_settled,
              details: item.details,
            }),
          })),
        }),
        {}
      )
    } else invoiceItem = null
    // // console.log(invoiceItem)
    // Transform advance_item
    if (apiResponse.advance_item?.[transactionId]) {
      advanceItem = Object.entries(apiResponse.advance_item[transactionId]).reduce(
        (acc, [ledgerId, items]) => ({
          ...acc,
          [ledgerId]: items.map((item) => ({
            ...transformKeys<AdvanceItem>({
              advance_id: item.advance_id,
              transaction_id: item.transaction_id,
              ledger_id: item.ledger_id,
              advance_number: item.advance_number,
              advance_date: item.advance_date,
              amount: item.amount,
              transaction_type: item.transaction_type,
              description: item.description,
              settled_amount: item.settled_amount,
              last_settled_amount: item.last_settled_amount,
              is_settled: item.is_settled,
              details: item.details,
            }),
          })),
        }),
        {}
      )
    } else advanceItem = null
    // // console.log(transactionId)
    // // console.log(apiResponse.against_invoice[transactionId])
    // Transform against_invoice
    if (apiResponse.against_invoice?.[transactionId]) {
      againstInvoice = Object.entries(apiResponse.against_invoice[transactionId]).reduce(
        (acc, [ledgerId, items]) => ({
          ...acc,
          [ledgerId]: items.map((item) => ({
            ...transformKeys<AgainstInvoice>({
              against_invoice_item_id: item.against_invoice_item_id,
              due_date: item.due_date,
              invoice_number: item.invoice_number,
              transaction_id: item.transaction_id,
              ledger_id: item.ledger_id,
              invoice_item_id: item.invoice_item_id,
              set_off_amount: item.set_off_amount,
              description: item.description,
              amount: item.amount,
              settled_amount: item.settled_amount,
              transaction_type: item.transaction_type,
            }),
          })),
        }),
        {}
      )
    } else againstInvoice = null
    // // console.log(againstInvoice)
    // Transform against_advance
    if (apiResponse.against_advance?.[transactionId]) {
      againstAdvance = Object.entries(apiResponse.against_advance[transactionId]).reduce(
        (acc, [ledgerId, items]) => ({
          ...acc,
          [ledgerId]: items.map((item) => ({
            ...transformKeys<AgainstAdvance>({
              against_advance_item_id: item.against_advance_item_id,
              amount: item.amount,
              advance_number: item.advance_number,
              advance_date: item.advance_date,
              transaction_id: item.transaction_id,
              transaction_type: item.transaction_type,
              ledger_id: item.ledger_id,
              advance_item_id: item.advance_item_id,
              set_off_amount: item.set_off_amount,
              settled_amount: item.settled_amount,
              description: item.description,
            }),
          })),
        }),
        {}
      )
    } else againstAdvance = null

    return {
      transactionId: transaction.transaction_id,
      companyId: transaction.company_id,
      voucherId: transaction.voucher_id,
      voucherNumber: transaction.voucher_number,
      currencyCode: transaction.currency_code,
      reportingCurrency: transaction.reporting_currency,
      exchangeMultiplier: transaction.exchange_multiplier,
      voucherDate: transaction.voucher_date,
      totalAmount: transaction.total_amount,
      narration: transaction.narration,
      transactionSummary: transaction.transaction_summary,
      isNaReference: transaction.is_na_reference,
      naReferenceAmount: transaction.na_reference_amount,
      voucherType: transaction.voucher_type,
      voucherName: transaction.voucher_name,
      invoiceItem: invoiceItem,
      advanceItem: advanceItem,
      againstInvoice: againstInvoice,
      againstAdvance: againstAdvance,
      transactionItems: transaction.transaction_item.map((item: any) => ({
        transactionItemId: item.transaction_item_id,
        transactionId: item.transaction_id,
        ledgerId: item.ledger_id,
        transactionType: item.transaction_type,
        transactionAmount: item.transaction_amount,
        userName: item.user_name,
      })),
    }
  })

  // Transform ledgers
  const transformedLedgers: Record<string, LedgerData> = {}
  Object.entries(apiResponse.ledger).forEach(([ledgerId, data]: [string, any]) => {
    transformedLedgers[ledgerId] = {
      ledgerId: data.ledger_id,
      companyId: data.company_id,
      groupId: data.group_id,
      parentLedger: data.parent_ledger,
      subLedger: data.sub_ledger,
      ledgerCode: data.ledger_code,
      isParentLedger: data.is_parent_ledger,
      currency: data.currency,
      email: data.email,
      personName: data.person_name,
      address: data.address,
      state: data.state,
      country: data.country,
      postalCode: data.postal_code,
      accountNumber: data.account_number,
      branchName: data.branch_name,
      ifscCode: data.ifsc_code,
      classification: data.classification,
      parentGroup: data.parent_group,
      subGroup: data.sub_group,
      openingBalance: data.opening_balance,
      closingBalance: data.closing_balance,
      creditAmount: data.credit_amount,
      debitAmount: data.debit_amount,
      currentBalance: data.current_balance,
      transactionType: data.transaction_type,
      asOnDate: data.as_on_date,
    }
  })
  console.log('transformed ledger ren')
  return {
    transactions: transformedTransactions,
    ledgers: transformedLedgers,
    openingBalance: apiResponse.opening_balance,
    closingBalance: apiResponse.closing_balance,
    debitAmount: apiResponse.total_debit,
    creditAmount: apiResponse.total_credit,
  }
}

// Updated API function
export const getTransactionsList = async (
  companyId: string,
  fy: string,
  voucherType: string
): Promise<TransformedResponse | undefined> => {
  try {
    const response = await axios.post(`${API_URL}/accounting/transaction/view`, {
      voucher_type:
        voucherType === 'creditnote'
          ? 'Credit Note'
          : voucherType === 'debitnote'
          ? 'Debit Note'
          : voucherType[0].toUpperCase() + voucherType.slice(1),
      fy_name: fy,
      company_id: Number(companyId),
      voucher_id: null,
      ledger_id: null,
      group_id: null,
    })
    // // console.log(response)
    // // console.log(transformResponse(response.data))
    return transformResponse(response.data)
  } catch (error) {
    console.error('Error fetching transactions:', error)
    return undefined
  }
}

export const getTransactionDetails = async (
  companyId: string,
  fy: string,
  voucherType: string,
  transactionId: number,
  voucherNumber: number
): Promise<{transaction: any} | undefined> => {
  return await axios
    .get(
      `${API_URL}/accounting/transactions/details/${companyId}/${fy}/${voucherType}?transaction_id=${transactionId}&voucher_number=${voucherNumber}`
    )
    .then((response: AxiosResponse<{transaction: any}>) => response.data)
    .then((response: {transaction: any}) => response)
}

export const deleteTransaction = async (
  transactionId: number
): Promise<{detail: string} | undefined> => {
  return await axios
    .delete(`${API_URL}/accounting/transaction/${transactionId}`)
    .then((response: AxiosResponse<{detail: string}>) => response.data)
    .then((response: {detail: string}) => response)
}
